import React from 'react';

export function AnalyticsStatus({ loading, error, isAuthenticated, analyticsData, onRetry, children }) {
  if (!isAuthenticated) {
    return (
      <div className="admin-dashboard">
        <div className="error">
          <h2>Access denied</h2>
          <p>{error || 'Admin privileges are required to view this dashboard.'}</p>
        </div>
      </div>
    );
  }

  if (loading && !analyticsData) {
    return (
      <div className="admin-dashboard">
        <div className="loading">Loading admin dashboard…</div>
      </div>
    );
  }

  if (error && !analyticsData) {
    return (
      <div className="admin-dashboard">
        <div className="error">
          <h2>Error</h2>
          <p>{error}</p>
          <button type="button" onClick={onRetry} className="btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="admin-dashboard">
        <div className="error">No analytics data available</div>
      </div>
    );
  }

  return children;
}

export function AppliedRangeSummary({ analyticsRangePreset, growthRangeApplied }) {
  if (analyticsRangePreset === 'today') {
    return (
      <span>
        Applied: <strong>Today (UTC)</strong> — {growthRangeApplied.start}
      </span>
    );
  }
  if (analyticsRangePreset === '7d') {
    return (
      <span>
        Applied: <strong>Last 7 days (UTC)</strong> — {growthRangeApplied.start} to {growthRangeApplied.end}
      </span>
    );
  }
  if (analyticsRangePreset === '30d') {
    return (
      <span>
        Applied: <strong>Last 30 days (UTC)</strong> — {growthRangeApplied.start} to {growthRangeApplied.end}
      </span>
    );
  }
  if (growthRangeApplied.start && growthRangeApplied.end) {
    return (
      <span>
        Applied: <strong>{growthRangeApplied.start}</strong> to <strong>{growthRangeApplied.end}</strong> (UTC)
      </span>
    );
  }
  return (
    <span>
      Applied: <strong>Last ~30 days (server default, rolling)</strong>
    </span>
  );
}

export function DateRangeToolbar({
  analyticsRangePreset,
  growthRangeApplied,
  growthRangeDraft,
  growthRangeError,
  lastUpdatedAt,
  loading,
  onPreset,
  onDraftChange,
  onApply,
  onClear,
  hint,
}) {
  return (
    <div className="chart" style={{ minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ marginBottom: 6 }}>Date window (UTC)</h2>
          <div style={{ fontSize: 13, color: 'var(--vx-admin-muted)' }}>
            <AppliedRangeSummary
              analyticsRangePreset={analyticsRangePreset}
              growthRangeApplied={growthRangeApplied}
            />
            {lastUpdatedAt ? (
              <span style={{ marginLeft: 12 }}>Last updated: {lastUpdatedAt.toLocaleString()}</span>
            ) : null}
          </div>
          {hint ? (
            <p className="adminMutedText" style={{ margin: '8px 0 0', fontSize: 13, maxWidth: '72ch' }}>
              {hint}
            </p>
          ) : null}
        </div>
      </div>

      <div className="adminGuestFunnel-range" style={{ marginTop: 14 }}>
        <span className="adminGuestFunnel-range-label adminMutedText">Range</span>
        <div className="adminGuestFunnel-range-pills">
          <button
            type="button"
            className={`adminRangePill${analyticsRangePreset === 'today' ? ' is-active' : ''}`}
            disabled={loading}
            onClick={() => onPreset('today')}
          >
            Today
          </button>
          <button
            type="button"
            className={`adminRangePill${analyticsRangePreset === '7d' ? ' is-active' : ''}`}
            disabled={loading}
            onClick={() => onPreset('7d')}
          >
            Last 7 days
          </button>
          <button
            type="button"
            className={`adminRangePill${analyticsRangePreset === '30d' ? ' is-active' : ''}`}
            disabled={loading}
            onClick={() => onPreset('30d')}
          >
            Last 30 days
          </button>
        </div>
      </div>

      <div className="adminDateRange" style={{ marginTop: 12 }}>
        <input
          type="date"
          className="adminDateInput"
          value={growthRangeDraft.start}
          onChange={onDraftChange('start')}
          disabled={loading}
          aria-label="Range start date (UTC)"
        />
        <span className="adminDateArrow adminMutedText">→</span>
        <input
          type="date"
          className="adminDateInput"
          value={growthRangeDraft.end}
          onChange={onDraftChange('end')}
          disabled={loading}
          aria-label="Range end date (UTC)"
        />
        <button type="button" onClick={onApply} className="btn" disabled={loading}>
          Apply
        </button>
        <button
          type="button"
          onClick={onClear}
          className="btn"
          disabled={loading || (!growthRangeApplied.start && !growthRangeApplied.end)}
        >
          Clear
        </button>
      </div>

      {growthRangeError ? (
        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--vx-admin-danger)' }}>
          {growthRangeError}
        </div>
      ) : null}
    </div>
  );
}
